<template>
    <div class="search-block">
        <input class="search-field" v-model.trim="searchQuery"
            type="text" placeholder="Поиск"
            @focus="active = true"
            @keydown.enter="goSearch()"
            >
        <button class="search-button" :class="{active: show && active}" @click="goSearch">
            <span>Найти</span>
            <img src="~/assets/icons/header/search.svg">
        </button>
    </div>

    <client-only>
        <ui-header-popup v-if="show" v-model="active" code="search-window">
            <div class="inner-body-block search-result">
                <div class="left">
                    <div class="block" v-if="foundSections.length > 1">
                        <div class="block-title">Искать в...</div>
                        <nuxt-link :to="getSearchUrl(item.code)" class="section-item" v-for="item of foundSections">
                            <div class="name">{{ item.name }}</div>
                            <div class="root" v-if="item.root">{{ item.root }}</div>
                        </nuxt-link>
                    </div>
                    <div class="block" v-else-if="recentSearch.length">
                        <div class="block-title">Вы искали</div>
                        <div class="section-item" v-for="item of recentSearch" @click="searchQuery = item">
                            <div class="name">{{ item }}</div>
                        </div>
                    </div>
                </div>
                <div class="right offers-grid" v-if="foundOffers.length">
                    <catalog-offer-search v-for="offer of foundOffers" :offer></catalog-offer-search>
                </div>
                <div class="right" v-else>
                    <div class="alert">Ничего не найдено</div>
                </div>
            </div>
        </ui-header-popup>
    </client-only>
</template>

<script lang="ts" setup>
import { debounce } from 'lodash-es';
import type { Ref } from 'vue';
import { useApi } from '~/lib';
import type { IOffer } from '~~/types/catalog';
import type { ISearchSection } from '~/types/search';
import { objectToUriString } from '~/lib/utls.js';
import { loadFromLocalStorageCompressed, saveToLocalStorageCompressed } from '~/lib/storage.js';

const searchQuery = ref('');
const foundOffers: Ref<IOffer[]> = ref([]);
const foundSections: Ref<ISearchSection[]> = ref([]);
const loading = ref(false);

const active = ref(false);
const show = ref(false);

const error = ref<string | null>(null);

const route = useRoute();
if (route.name === 'search' && typeof route.query.q === 'string') {
    searchQuery.value = route.query.q.trim();
}
const recentSearch = ref<string[]>([]);

function loadRecentSearch() {
    const data = loadFromLocalStorageCompressed('recent-search');
    if (data) {
        recentSearch.value = data;
    }
}

function addRecentSearch(query: string) {
    if (recentSearch.value.includes(query)) {
        const idx = recentSearch.value.indexOf(query);
        recentSearch.value.splice(idx, 1);
        recentSearch.value.unshift(query);
    } else {
        recentSearch.value.unshift(query);
        recentSearch.value = recentSearch.value.slice(0, 10);
    }

    saveToLocalStorageCompressed('recent-search', recentSearch.value);
}

onMounted(() => {
    loadRecentSearch();
})

const api = useApi();

const debouncedSearch = debounce(() => {
    search(searchQuery.value);
}, 300)

const router = useRouter();
router.afterEach(() => {
    active.value = false;
});

watch(searchQuery, () => {
    if (!searchQuery.value.length) {
        show.value = false;
    }

    debouncedSearch()
});

const searchId = ref<number | null>(null);

async function search(query: string) {

    if (query.length < 3 || loading.value) {
        foundOffers.value = [];
        foundSections.value = [];
        searchId.value = null;
        // fillSearchItems();
        return;
    }

    loading.value = true;

    try {

        const data = await api.request('/rest/catalog/search/mini', {
            search: query,
        });

        show.value = true;

        foundOffers.value = data.offers;
        foundSections.value = data.sections;
        searchId.value = data.searchId;
    } catch (err) {
        console.error(err);

        foundOffers.value = [];
        foundSections.value = [];
    }

    loading.value = false;

    // если строка запроса изменилась, пока мы искали
    if (query !== searchQuery.value) {
        // то мы ищем снова
        search(searchQuery.value);
    } else if (foundOffers.value) {
        addRecentSearch(query);
    }
}

function getSearchUrl(section?: string) {
    const params: any = {
        q: toValue(searchQuery)
    };

    if (section) {
        params.section = section;
    }

    if (searchId.value) {
        params._id = toValue(searchId);
    }
    return `/search/?${objectToUriString(params)}`
}

function goSearch() {
    if (searchQuery.value.length > 2) {
        router.push(getSearchUrl());
    }
}

</script>

<style lang="scss" scoped>

@use '~/assets/css/colors.scss' as *;

.search-block {
    flex: 1;
    position: relative;
}

.search-field {
    width: 100%;
    height: 38px;
    background-color: #fff;
    border: 1px solid var(--brdr0);
    border-radius: 19px;
    font-size: 18px;
    color: var(--t0);
    padding-left: 21px;

    &::placeholder {
        font-weight: 300;
        color: var(--t2);
        // color: black;
        // opacity: .7;
    }

    &:focus-within,
    &:focus {
        outline: none;
        border: 1px solid var(--orange);
        // padding-left: 21px;
    }
}

.search-button {
    position: absolute;
    right: 0;
    background: transparent;
    outline: none;
    border: none;
    top: 4px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 30px;
    height: 30px;
    // width: 30px;
    cursor: pointer;
    transition: background .1s, gap .1s, border-color .1s, width .1s;
    padding: 0 8px;
    border: 1px solid transparent;

    span {
        width: 0;
        overflow: hidden;
        transition: width .1s;
        color: var(--t1);
        font-weight: 300;
        // color: white
    }

    &.active {
        width: 84px;
        gap: 8px;
        border-color: var(--orange);

        span {
            width: 41px;
        }
    }

    &:hover {
        background: var(--bgy0);
    }
    &:active {
        background: var(--bgy1);
    }
}

.search-result {
    display: flex;
}

.left {
    width: 250px;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-right: 1px solid var(--brdr0);
    padding: 30px 24px 30px 0;
    flex-shrink: 0;
    overflow: auto;
    max-height: 420px;
    scrollbar-width: thin;
}

.right {
    flex: 1;
    width: 100px;
    padding: 24px 0 24px 60px;
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    align-items: flex-start;
    // overflow-x: auto;
    height: min-content;
    max-height: calc(100vh - var(--pt) - 1px);
}

.block {

    .block-title {
        font-size: 18px;
        font-weight: 550;
        margin-bottom: 10px;
    }
}

.section-item {
    text-decoration: none;
    font-size: 14px;
    display: block;
    user-select: none;
    // margin-bottom: 4px;

    padding: 2px 8px;
    border-radius: 4px;
    margin: 4px -8px;
    transition: background-color .1s;

    &:hover {
        background: var(--bgy0);
    }

    .root {
        color: var(--t1);
        font-weight: 300;
        font-size: 12px;
    }

    .name {
        color: var(--t0);
    }
}

.offers-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 20px;
    width: 100%;
}

.alert {
    background: var(--bg2);
    padding: 6px 12px;
    border-radius: 4px;
    width: 100%;
}

</style>